import * as Sentry from "@sentry/vue";

import { useProject } from "@/stores/project/project.store";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";

/**
 * Re-reads the project the background Speckle copy just filled, so the 3D viewer appears without a
 * page reload (RTLCA-5478).
 *
 * The copy populates `referencedObjectId` on every model, which is the whole condition the "3D
 * Model" nav item hangs on. But both stores that hold those models are filled by REST calls made on
 * project entry and on a revision switch — nothing re-reads them when a background job lands, so the
 * client kept its pre-copy nulls and the item stayed hidden until the page was reloaded.
 *
 * BOTH reads are needed, for two different gates:
 *   - the project store's `models` feeds `hasModelElements` — the nav item itself, desktop and mobile;
 *   - the ProjectWrapper's `models` feeds the dashboard's `hasAvailableModels` — the watcher that
 *     flips `enableViewer` back off, which would bounce the user straight back to the dashboard the
 *     moment they clicked the newly appeared item.
 *
 * `refreshProjectWrapper` rather than `forceUpdateProjectWrapperAndAdditionalInfo`: only the models
 * went stale, and the additional-info read is the expensive half.
 */
export async function refreshCopiedProject(projectId: string): Promise<void> {
  const projectStore = useProject();

  // Both stores hold exactly one project at a time. The copy toasts are app-wide and unfiltered, so
  // most copies that land belong to some other project — refreshing then would throw away whatever
  // the user is actually looking at and read the wrong project into it.
  if (projectStore.project?.id !== projectId) return;

  try {
    await Promise.all([
      // Forced: both calls return early on the project id they already hold.
      projectStore.fetchProject(projectId, true),
      useProjectWrapper().refreshProjectWrapper(projectId, true),
    ]);
  } catch (error: unknown) {
    // Best-effort, like the toasts themselves. A refresh that fails degrades to the behaviour this
    // exists to improve — the viewer appears on the next reload — and must not surface a second,
    // contradictory toast next to "Speckle project copied".
    Sentry.captureException(error);
  }
}
